import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import itSecurityVideo from '../../assets/videos/services/IT_Security.mp4';
import hvacVideo from '../../assets/videos/services/HAVC_Cooling_Technology_Visualization.mp4';
import fireVideo from '../../assets/videos/services/FIRE.mp4';
import maintenanceVideo from '../../assets/videos/services/Maintenance.mp4';

interface HeroMediaItem {
  id: string;
  videoSrc: string;
  categoryKey: string;
  titleKey: string;
  descriptionKey: string;
  buttonKey: string;
}

const heroMedia: HeroMediaItem[] = [
  {
    id: 'it-security',
    videoSrc: itSecurityVideo,
    categoryKey: 'hero.itSecurity.category',
    titleKey: 'hero.itSecurity.title',
    descriptionKey: 'hero.itSecurity.description',
    buttonKey: 'hero.itSecurity.button',
  },
  {
    id: 'hvac-solutions',
    videoSrc: hvacVideo,
    categoryKey: 'hero.hvac.category',
    titleKey: 'hero.hvac.title',
    descriptionKey: 'hero.hvac.description',
    buttonKey: 'hero.hvac.button',
  },
  {
    id: 'fire-safety',
    videoSrc: fireVideo,
    categoryKey: 'hero.fireSafety.category',
    titleKey: 'hero.fireSafety.title',
    descriptionKey: 'hero.fireSafety.description',
    buttonKey: 'hero.fireSafety.button',
  },
  {
    id: 'maintenance',
    videoSrc: maintenanceVideo,
    categoryKey: 'hero.maintenance.category',
    titleKey: 'hero.maintenance.title',
    descriptionKey: 'hero.maintenance.description',
    buttonKey: 'hero.maintenance.button',
  }
];

const heroStats = [
  { value: '7000+', labelKey: 'heroSectionMain.stats.camerasInstalled' },
  { value: '250+', labelKey: 'heroSectionMain.stats.alarmsDeployed' },
  { value: '100+', labelKey: 'heroSectionMain.stats.warehousesSecured' },
  { value: '30', labelKey: 'heroSectionMain.stats.globalPartners' }
];

const parseStatValue = (value: string): { target: number; suffix: string } => {
  const numeric = parseInt(value.replace(/[^0-9]/g, ''), 10);
  const suffix = value.replace(/[0-9]/g, '');
  return { target: Number.isNaN(numeric) ? 0 : numeric, suffix };
};

const HeroSectionMain: React.FC = () => {
  const { t } = useTranslation();
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [animatedStats, setAnimatedStats] = useState<number[]>(
    heroStats.map(() => 1)
  );
  const [shouldAnimateStats, setShouldAnimateStats] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentIndexRef = useRef(0);
  const statsRef = useRef<HTMLDivElement>(null);
  const currentSlide = heroMedia[currentVideoIndex];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const loadAndPlay = async () => {
      try {
        video.load();
        video.muted = true;
        await video.play();
      } catch (error) {
        console.error('Error loading hero video:', error);
      }
    };

    loadAndPlay();
  }, [currentVideoIndex]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const handleCanPlay = () => {
      video.play().catch((error) => {
        console.error('Error autoplaying hero video:', error);
      });
    };

    video.addEventListener('canplay', handleCanPlay);
    video.muted = true;
    video.play().catch((error) => {
      console.error('Error starting hero video:', error);
    });

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  useEffect(() => {
    // Preload all videos to minimize blank frames between switches.
    const preloaders = heroMedia.map((item) => {
      const el = document.createElement('video');
      el.src = item.videoSrc;
      el.preload = 'auto';
      el.muted = true;
      el.playsInline = true;
      el.load();
      return el;
    });

    return () => {
      preloaders.forEach((el) => {
        el.pause();
        el.removeAttribute('src');
        el.load();
      });
    };
  }, []);

  useEffect(() => {
    const target = statsRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setShouldAnimateStats(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldAnimateStats) return;

    const start = performance.now();
    const duration = 1400;
    let frameId = 0;

    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setAnimatedStats(
        heroStats.map((stat) => {
          const { target } = parseStatValue(stat.value);
          if (target <= 1) return target;
          return Math.max(1, Math.round(1 + (target - 1) * eased));
        })
      );

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animate);
      }
    };

    frameId = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frameId);
  }, [shouldAnimateStats]);

  const handleVideoEnded = () => {
    const nextIndex = (currentIndexRef.current + 1) % heroMedia.length;
    currentIndexRef.current = nextIndex;
    setCurrentVideoIndex(nextIndex);
  };

  const handleDotClick = (index: number) => {
    if (index === currentIndexRef.current) return;
    currentIndexRef.current = index;
    setCurrentVideoIndex(index);
  };

  return (
    <section
      className="relative min-h-screen overflow-hidden bg-[#0a1628]"
    >
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={handleVideoEnded}
          className="absolute inset-0 h-full w-full object-cover object-[72%_center] opacity-60 saturate-100 brightness-90 md:object-[66%_center] md:opacity-50"
        >
          <source src={heroMedia[currentVideoIndex].videoSrc} type="video/mp4" />
          {t('heroSectionMain.videoFallback', { defaultValue: 'Your browser does not support the video tag.' })}
        </video>
        <div
          className="absolute inset-0 bg-[#0a1628]/70"
        />
        <div className="pointer-events-none absolute left-[12%] top-[18%] h-[260px] w-[260px] rounded-full bg-[rgba(41,200,193,0.14)] opacity-55 blur-[80px]" />
        <div className="pointer-events-none absolute right-[10%] top-[24%] h-[340px] w-[340px] rounded-full bg-[rgba(66,126,196,0.13)] opacity-55 blur-[80px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 text-teal-400 text-base font-semibold px-4 py-2 rounded-full mb-8 tracking-wide">
            <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse" />
            {t('heroSectionMain.badge', { defaultValue: 'TRUSTED SYSTEM INTEGRATOR IN THE DRC' })}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] font-bold text-white leading-[1.08] tracking-tight">
            {t(currentSlide.titleKey, { defaultValue: 'Integrated Technology Solutions' })}
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-white/50 leading-relaxed max-w-xl font-light">
            {t(currentSlide.descriptionKey, {
              defaultValue: 'End-to-end engineering and managed services tailored to your operational environment.',
            })}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-teal-500 hover:bg-teal-400 text-white font-semibold px-8 py-4 transition-all text-sm hover:shadow-lg hover:shadow-teal-500/20 hover:-translate-y-0.5"
            >
              {t(currentSlide.buttonKey, { defaultValue: 'Contact Now' })}
              {FiArrowRight({ 'aria-hidden': true, className: 'h-5 w-5' })}
            </Link>
            <Link
              to="/solutions"
              className="inline-flex items-center justify-center border border-white/15 hover:border-white/30 text-white font-medium px-8 py-4 transition-all text-sm hover:bg-white/5"
            >
              {t('heroSectionMain.ctaSecondary', { defaultValue: 'Explore Solutions' })}
            </Link>
          </div>

        </div>

        <div
          ref={statsRef}
          className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-24 pt-10 border-t border-white/5"
          aria-label={t('heroSectionMain.statsAria', { defaultValue: 'Company highlights' })}
        >
          {heroStats.map((stat, index) => (
            <div className="group" key={stat.labelKey}>
              <div className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                {animatedStats[index]}
                {parseStatValue(stat.value).suffix}
              </div>
              <div className="text-xs text-white/30 mt-2 tracking-wide uppercase font-medium">
                {t(stat.labelKey, { defaultValue: '' })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2" aria-label="Hero slide indicators">
        {heroMedia.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`h-2.5 w-2.5 rounded-full transition-all ${
              index === currentVideoIndex ? 'bg-teal-400 w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSectionMain;
